'use client'

import { useEffect, useState } from 'react'
import { useRouter } from 'next/navigation'
import Link from 'next/link'
import { Camera, Award, MapPin, Phone, Mail, Edit2, LogOut, X, GraduationCap, Calendar, ChevronRight, LayoutDashboard } from 'lucide-react'
import { getSupabaseClient } from '@/utils/supabase/singleton'
import { getLevelDisplayName } from '@/data/certificationLevels'
import { useTheme } from '@/contexts/ThemeContext'
import BuddyTemperature from '@/components/BuddyTemperature'
import { parseAuthToken } from '@/utils/base64'
import logger from '@/utils/logger'

interface UserProfile {
  kakao_id: string
  name: string
  email: string
  phone: string
  certification_agency: string
  certification_level: string
  region: string
  profile_image: string
  certification_image_url: string
  is_student: boolean
  profile_completed: boolean
  buddy_temperature: number
  created_at: string
  updated_at: string
}

export default function ProfilePage() {
  const router = useRouter()
  const { theme } = useTheme()
  const isDark = theme === 'dark'
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [subscription, setSubscription] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [showCertPreview, setShowCertPreview] = useState(false)
  const [isInstructor, setIsInstructor] = useState(false)
  const [instructorProfileId, setInstructorProfileId] = useState<string | null>(null)
  const [isInstructorActive, setIsInstructorActive] = useState(true)
  const [showPauseConfirm, setShowPauseConfirm] = useState(false)
  const [isTogglingActive, setIsTogglingActive] = useState(false)
  const [reservations, setReservations] = useState<any[]>([])

  const bg = isDark ? '#0f172a' : '#f4f7fb'
  const card = isDark ? '#1e2a3d' : '#ffffff'
  const border = isDark ? '#2c3a52' : '#e9eff7'
  const ink = isDark ? '#e8f0fb' : '#0a1f3a'
  const secondary = isDark ? '#94a3b8' : '#6b7684'
  const accent = isDark ? '#5ea6ff' : '#2b6df0'

  useEffect(() => {
    loadProfile()
    loadReservations()
  }, [])

  const loadReservations = async () => {
    try {
      const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null
      if (!token) return
      const res = await fetch('/api/tours/reserve', {
        headers: { 'Authorization': `Bearer ${token}` },
      })
      if (!res.ok) return
      const json = await res.json()
      setReservations(json.data || [])
    } catch { /* silent */ }
  }

  const loadProfile = async () => {
    try {
      const token = localStorage.getItem('auth_token')
      if (!token) { router.push('/login'); return }

      const decoded = parseAuthToken(token)
      const supabase = getSupabaseClient()

      let query = supabase.from('user_profiles').select('*')
      if (decoded.kakao_id) {
        query = query.eq('kakao_id', decoded.kakao_id)
      } else if (decoded.naver_id) {
        query = query.eq('naver_id', decoded.naver_id)
      } else {
        logger.error('No kakao_id or naver_id in token')
        router.push('/login')
        return
      }

      const { data, error } = await query.single()
      if (error || !data) {
        logger.error('Profile not found:', error)
        localStorage.removeItem('auth_token')
        window.location.href = '/'
        return
      }

      setProfile(data)

      const { data: instructorData } = await supabase
        .from('instructor_profiles')
        .select('id, is_active')
        .eq('user_id', data.id)
        .single()

      if (instructorData) {
        setIsInstructor(true)
        setInstructorProfileId(instructorData.id)
        setIsInstructorActive(instructorData.is_active !== false)
      }
    } catch (error) {
      logger.error('Error:', error)
      router.push('/login')
    } finally {
      setIsLoading(false)
    }
  }

  const handleLogout = () => {
    localStorage.removeItem('auth_token')
    router.push('/')
  }

  const handleToggleInstructorActive = async (activate: boolean) => {
    if (!instructorProfileId) return
    setIsTogglingActive(true)
    try {
      const supabase = getSupabaseClient()
      const { error } = await supabase
        .from('instructor_profiles')
        .update({ is_active: activate })
        .eq('id', instructorProfileId)
      if (error) throw error
      setIsInstructorActive(activate)
    } catch {
      alert('처리 중 오류가 발생했습니다.')
    } finally {
      setIsTogglingActive(false)
      setShowPauseConfirm(false)
    }
  }

  if (isLoading) {
    return (
      <div style={{ background: bg, minHeight: '100vh' }} className="flex items-center justify-center">
        <div className="animate-pulse text-center">
          <div className="w-20 h-20 rounded-full mx-auto mb-4" style={{ background: card }} />
          <div className="h-4 w-32 rounded mx-auto" style={{ background: card }} />
        </div>
      </div>
    )
  }

  if (!profile) {
    return (
      <div style={{ background: bg, minHeight: '100vh' }} className="flex items-center justify-center">
        <p style={{ color: secondary }}>프로필을 찾을 수 없습니다.</p>
      </div>
    )
  }

  return (
    <div style={{ background: bg, minHeight: '100vh' }}>
      <div className="max-w-2xl mx-auto px-5 py-6 pb-10">

        {/* 헤더 액션 */}
        <div className="flex items-center justify-between mb-3">
          <h1 style={{ font: '700 18px/26px sans-serif', color: ink }}>내 프로필</h1>
          <div className="flex items-center gap-2">
            <button
              onClick={() => router.push('/profile/edit')}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium"
              style={{ background: card, color: ink, border: `1px solid ${border}` }}
            >
              <Edit2 className="w-3.5 h-3.5" />
              수정
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-medium"
              style={{ background: isDark ? 'rgba(239,68,68,0.12)' : '#fef2f2', color: '#ef4444', border: `1px solid ${isDark ? 'rgba(239,68,68,0.2)' : '#fecaca'}` }}
            >
              <LogOut className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* 강사 퀵 메뉴 — 강사일 때만 표시 */}
        {isInstructor && (
          <div className="chip-rail mb-4">
            <Link
              href="/instructor/dashboard"
              className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold"
              style={{ background: isDark ? '#1e3a5f' : '#0a1f3a', color: '#fff' }}
            >
              <LayoutDashboard className="w-3.5 h-3.5" />
              강사 홈
            </Link>
            <button
              onClick={() => router.push('/instructor/edit')}
              className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium"
              style={{ background: card, color: accent, border: `1px solid ${border}` }}
            >
              <GraduationCap className="w-3.5 h-3.5" />
              강사 프로필
            </button>
            <button
              onClick={() => isInstructorActive ? setShowPauseConfirm(true) : handleToggleInstructorActive(true)}
              disabled={isTogglingActive}
              className="flex-shrink-0 px-3 py-2 rounded-xl text-xs font-medium disabled:opacity-60"
              style={{
                background: isInstructorActive
                  ? (isDark ? 'rgba(251,146,60,0.15)' : '#fff7ed')
                  : (isDark ? 'rgba(34,197,94,0.15)' : '#f0fdf4'),
                color: isInstructorActive ? '#f97316' : '#16a34a',
                border: `1px solid ${isInstructorActive
                  ? (isDark ? 'rgba(251,146,60,0.3)' : '#fed7aa')
                  : (isDark ? 'rgba(34,197,94,0.3)' : '#bbf7d0')}`,
              }}
            >
              {isTogglingActive ? '처리 중...' : isInstructorActive ? '활동 중지' : '활동 재개'}
            </button>
          </div>
        )}

        {/* 프로필 카드 */}
        <div className="rounded-2xl overflow-hidden mb-4" style={{ background: card, border: `1px solid ${border}` }}>
          {/* 프로필 상단 배너 */}
          <div className="px-6 py-6" style={{ background: isDark ? '#1a2d4a' : '#0a1f3a' }}>
            <div className="flex items-center gap-5">
              <div
                className="w-20 h-20 rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center"
                style={{ background: isDark ? '#2c3a52' : 'rgba(255,255,255,0.15)', border: '2px solid rgba(255,255,255,0.2)' }}
              >
                {profile.profile_image ? (
                  <img src={profile.profile_image} alt={profile.name} className="w-full h-full object-cover" />
                ) : (
                  <Camera className="w-8 h-8" style={{ color: 'rgba(255,255,255,0.5)' }} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h2 style={{ font: '700 20px/28px sans-serif', color: '#ffffff' }}>{profile.name}</h2>
                  {subscription && (
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium"
                      style={{ background: 'rgba(255,255,255,0.15)', color: '#fff' }}>
                      {subscription.currentPlan === 'premium' ? 'Premium' : subscription.currentPlan === 'standard' ? 'Standard' : '무료'}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {profile.is_student ? (
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium"
                      style={{ background: 'rgba(251,191,36,0.2)', color: '#fbbf24' }}>
                      교육생
                    </span>
                  ) : (
                    <div className="flex items-center gap-1">
                      <Award className="w-3.5 h-3.5" style={{ color: 'rgba(255,255,255,0.6)' }} />
                      <span style={{ font: '400 13px/20px sans-serif', color: 'rgba(255,255,255,0.8)' }}>
                        {getLevelDisplayName(profile.certification_level)}
                      </span>
                    </div>
                  )}
                </div>
                <div className="mt-2">
                  <BuddyTemperature temperature={profile.buddy_temperature || 36.5} variant="inline" />
                </div>
              </div>
            </div>
          </div>

          {/* 상세 정보 */}
          <div className="px-6 py-5 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: isDark ? '#1e3a5f' : '#eff6ff' }}>
                  <Phone className="w-4 h-4" style={{ color: accent }} />
                </div>
                <div>
                  <p style={{ font: '400 11px/16px sans-serif', color: secondary }}>전화번호</p>
                  <p style={{ font: '500 13px/20px sans-serif', color: ink }}>{profile.phone || '—'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: isDark ? '#1e3a5f' : '#eff6ff' }}>
                  <Mail className="w-4 h-4" style={{ color: accent }} />
                </div>
                <div>
                  <p style={{ font: '400 11px/16px sans-serif', color: secondary }}>이메일</p>
                  <p style={{ font: '500 13px/20px sans-serif', color: ink }} className="truncate">{profile.email || '—'}</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ background: isDark ? '#1e3a5f' : '#eff6ff' }}>
                <MapPin className="w-4 h-4" style={{ color: accent }} />
              </div>
              <div>
                <p style={{ font: '400 11px/16px sans-serif', color: secondary }}>활동 지역</p>
                <p style={{ font: '500 13px/20px sans-serif', color: ink }}>{profile.region || '—'}</p>
              </div>
            </div>

            {profile.certification_image_url && (
              <div className="pt-2" style={{ borderTop: `1px solid ${border}` }}>
                <p style={{ font: '500 12px/18px sans-serif', color: secondary, marginBottom: 8 }}>자격증</p>
                <div
                  className="rounded-xl overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
                  style={{ border: `1px solid ${border}` }}
                  onClick={() => setShowCertPreview(true)}
                >
                  <img src={profile.certification_image_url} alt="자격증" className="w-full max-w-md mx-auto block" />
                  <p className="text-center py-2" style={{ font: '400 11px/16px sans-serif', color: secondary }}>
                    클릭하여 크게 보기
                  </p>
                </div>
              </div>
            )}

            <div className="pt-2" style={{ borderTop: `1px solid ${border}` }}>
              <p style={{ font: '400 11px/16px sans-serif', color: secondary }}>
                가입일: {new Date(profile.created_at).toLocaleDateString('ko-KR')}
              </p>
            </div>
          </div>
        </div>

        {reservations.length > 0 && (
          <div className="rounded-2xl overflow-hidden mb-4" style={{ background: card, border: `1px solid ${border}` }}>
            <div className="px-5 py-4" style={{ borderBottom: `1px solid ${border}` }}>
              <p style={{ font: '600 14px/22px sans-serif', color: ink }}>내 투어 예약</p>
            </div>
            <div className="divide-y" style={{ borderColor: border }}>
              {reservations.slice(0, 3).map((r: any) => (
                <Link
                  key={r.id}
                  href={`/tours/${r.diving_tours?.id}`}
                  className="flex items-center justify-between px-5 py-3 hover:opacity-80 transition-opacity"
                >
                  <div className="min-w-0 flex-1">
                    <p style={{ font: '500 13px/20px sans-serif', color: ink }} className="truncate">
                      {r.diving_tours?.title || '투어'}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {r.diving_tours?.start_date && (
                        <span className="flex items-center gap-1" style={{ font: '400 11px/16px sans-serif', color: secondary }}>
                          <Calendar className="w-3 h-3" />
                          {new Date(r.diving_tours.start_date).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' })}
                        </span>
                      )}
                      <span className="px-1.5 py-0.5 rounded-full text-xs"
                        style={{
                          background: r.status === 'confirmed'
                            ? (isDark ? 'rgba(34,197,94,0.15)' : '#f0fdf4')
                            : r.status === 'cancelled'
                              ? (isDark ? 'rgba(107,114,128,0.15)' : '#f3f4f6')
                              : (isDark ? 'rgba(251,191,36,0.15)' : '#fefce8'),
                          color: r.status === 'confirmed' ? '#16a34a'
                            : r.status === 'cancelled' ? secondary
                            : '#ca8a04',
                        }}>
                        {r.status === 'confirmed' ? '확정' : r.status === 'cancelled' ? '취소됨' : '대기 중'}
                      </span>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 flex-shrink-0 ml-2" style={{ color: secondary }} />
                </Link>
              ))}
            </div>
          </div>
        )}

        {!isInstructor && (
          <button
            onClick={() => router.push('/instructor/register')}
            className="w-full rounded-2xl p-5 text-left transition-all"
            style={{ background: card, border: `1px solid ${border}` }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p style={{ font: '400 12px/18px sans-serif', color: secondary, marginBottom: 2 }}>프리다이빙 강사이신가요?</p>
                <p style={{ font: '700 15px/22px sans-serif', color: ink }}>강사로 활동하기 →</p>
              </div>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: isDark ? '#1e3a5f' : '#eff6ff' }}>
                <GraduationCap className="w-5 h-5" style={{ color: accent }} />
              </div>
            </div>
          </button>
        )}
      </div>

      {showCertPreview && profile.certification_image_url && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setShowCertPreview(false)}
        >
          <div className="relative max-w-4xl max-h-[90vh] w-full">
            <img src={profile.certification_image_url} alt="자격증" className="w-full h-auto max-h-[85vh] object-contain mx-auto rounded-xl" />
            <button onClick={() => setShowCertPreview(false)} className="absolute top-2 right-2 p-2 rounded-full" style={{ background: 'rgba(0,0,0,0.6)' }}>
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>
      )}

      {showPauseConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
          <div className="w-full max-w-sm rounded-2xl p-6" style={{ background: card }}>
            <h3 style={{ font: '700 16px/24px sans-serif', color: ink, marginBottom: 8 }}>활동을 중지 하시겠습니까?</h3>
            <p style={{ font: '400 13px/20px sans-serif', color: secondary, marginBottom: 24 }}>
              활동 중지 시 강사 목록에서 프로필이 숨겨집니다. 언제든지 다시 활성화할 수 있습니다.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setShowPauseConfirm(false)} className="flex-1 py-2.5 rounded-xl font-medium text-sm"
                style={{ background: isDark ? '#2c3a52' : '#f3f4f6', color: ink }}>아니오</button>
              <button onClick={() => handleToggleInstructorActive(false)} disabled={isTogglingActive}
                className="flex-1 py-2.5 rounded-xl font-medium text-sm disabled:opacity-50"
                style={{ background: '#f97316', color: '#fff' }}>
                {isTogglingActive ? '처리 중...' : '중지'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
